/**
 * Contracts of the help-desk tracking endpoints (stork-api), used by the support route viewer in
 * tracking-system-admin. Positions are returned raw: no accuracy filter, no simplification.
 */

export interface IHelpDeskTrackingDevice {
    /** Dolphin device id: the one positions are requested with. */
    id: number;
    code?: string;
    name?: string;
    licensePlate?: string;
    identification?: { type: string; value: string };
    /** `vehicles` or `drivers`: which stork collection `externalId` points to. */
    externalIdSource?: string;
    externalId?: string;
    organizationId?: string;
    platformName?: string;
    enabled?: boolean;
    lastReport?: string;
    lastPositionUpdate?: string;
}

export interface IHelpDeskTrackingVehicle {
    id: string;
    name?: string;
    licensePlate?: string;
    externalId?: string;
}

export interface IHelpDeskTrackingDriver {
    id: string;
    fullName?: string;
    phoneNumber?: string;
    nationalId?: string;
    externalId?: string;
    code?: string;
}

export enum HelpDeskTrackingMatchField {
    DEVICE_CODE = 'deviceCode',
    DEVICE_IDENTIFICATION = 'deviceIdentification',
    LICENSE_PLATE = 'licensePlate',
    VEHICLE_NAME = 'vehicleName',
    DRIVER_NAME = 'driverName',
    DRIVER_PHONE = 'driverPhone',
    DRIVER_NATIONAL_ID = 'driverNationalId',
    DRIVER_CODE = 'driverCode',
    EXTERNAL_ID = 'externalId',
}

/** One trackable unit: a stork vehicle or driver and/or its Dolphin device. `device` is absent when it was never linked. */
export interface IHelpDeskTrackingDeviceCandidate {
    organizationId: string;
    vehicle?: IHelpDeskTrackingVehicle;
    driver?: IHelpDeskTrackingDriver;
    device?: IHelpDeskTrackingDevice;
    matchedBy: HelpDeskTrackingMatchField[];
}

export interface IHelpDeskTrackingRange {
    from: string;
    to: string;
}

export interface IHelpDeskTrackingPositionsInput {
    deviceIds: number[];
    ranges: IHelpDeskTrackingRange[];
}

export interface IHelpDeskTrackingPosition {
    id: number;
    lat: number;
    lng: number;
    /** When the device says the fix was taken. */
    timestamp: string;
    /** When Dolphin stored it: `insertedAt - timestamp` is the reporting lag. */
    insertedAt?: string;
    speed?: number;
    heading?: number;
    /** Raw Dolphin metadata: satelliteCount, accuracy, batteryLevel, appVersion... */
    metadata?: Record<string, unknown>;
}

export interface IHelpDeskTrackingPositionsSeries {
    deviceId: number;
    range: IHelpDeskTrackingRange;
    positions: IHelpDeskTrackingPosition[];
    /** Dolphin had more positions than the per-series cap allows; only the first ones are returned. */
    truncated: boolean;
    total: number;
}

export interface IHelpDeskTrackingPositionsOutput {
    series: IHelpDeskTrackingPositionsSeries[];
}

export interface IHelpDeskTrackingZone {
    id: string;
    name?: string;
    type?: string;
    /** GeoJSON geometry, as stored on the stork zone. */
    geometry?: unknown;
}

export interface IHelpDeskTrackingCheckpoint {
    index: number;
    type?: string;
    name?: string;
    lat?: number;
    lng?: number;
    plannedAt?: string;
    locationName?: string;
    zones: IHelpDeskTrackingZone[];
    steps: { type?: string; name?: string; plannedAt?: string; completedAt?: string }[];
}

/** A stretch of the trip and the device that answers for it: the trip has one per unit change. */
export interface IHelpDeskTrackingLeg {
    range: IHelpDeskTrackingRange;
    vehicle?: IHelpDeskTrackingVehicle;
    driver?: IHelpDeskTrackingDriver;
    /** Absent when the leg's unit has no Dolphin device for the trip's reporting type. */
    device?: IHelpDeskTrackingDevice;
}

export interface IHelpDeskTripTrackingContext {
    id: string;
    tripCode: string;
    organizationId: string;
    reportingType?: string;
    status?: string;
    scheduledStart?: string;
    scheduledFinish?: string;
    realStart?: string;
    realFinish?: string;
    /** Found only in the archive cluster: the live collection keeps about a month of trips. */
    fromArchive: boolean;
    legs: IHelpDeskTrackingLeg[];
    checkpoints: IHelpDeskTrackingCheckpoint[];
}
